// The site shell: the header, the page, the footer, and the three modals the
// header opens (CLAUDE.md: One design system across the whole site).
//
// Every page sits in the same 720px column, so pressing Play never shifts the
// layout. The header stays visible behind an open modal, dimmed by the overlay.
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { SiteHeader } from './components/SiteHeader.tsx';
import { SiteFooter } from './components/SiteFooter.tsx';
import { Modal } from './components/Modal.tsx';
import { StatsPanel } from './components/StatsPanel.tsx';
import { ArchivePanel } from './components/ArchivePanel.tsx';
import { HowToPlayPanel } from './components/HowToPlayPanel.tsx';
import { Index } from './pages/Index.tsx';
import { Notice } from './pages/Notice.tsx';
import { About } from './pages/About.tsx';
import { Contact } from './pages/Contact.tsx';
import { Privacy } from './pages/Privacy.tsx';
import { DesignSystem } from './pages/DesignSystem.tsx';
import { RunningOrderPage } from './games/running-order/RunningOrderGame.tsx';
import { CallSheetPage } from './games/call-sheet/CallSheetGame.tsx';
import { flushPending } from './lib/history.ts';
import { DEVICE_KEY } from './lib/device.ts';
import { firstVisit, gameOfPath, type HowToPlayGame } from './lib/how-to-play.ts';

export function App() {
  const [open, setOpen] = useState<'archive' | 'stats' | 'howto' | null>(null);
  const [howTab, setHowTab] = useState<HowToPlayGame>('running-order');
  const location = useLocation();
  const navigate = useNavigate();
  const game = gameOfPath(location.pathname);

  // **How to play opens on the tab for the game you are on**, and on Running
  // Order anywhere else, the home page included.
  const openHowToPlay = (tab: HowToPlayGame = game ?? 'running-order') => {
    setHowTab(tab);
    setOpen('howto');
  };

  // **And by itself, the first time this device opens each game** — however it
  // got there, a shared link included — or when an old How it works address sent
  // it here (`howToPlay` in the history state, which is then cleared, so a
  // refresh does not open it again).
  useEffect(() => {
    if (!game) return;
    const asked = (location.state as { howToPlay?: boolean } | null)?.howToPlay;
    if (asked) navigate(location.pathname + location.search, { replace: true, state: null });
    if (firstVisit(game) || asked) openHowToPlay(game);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.state]);

  // Results an earlier visit finished but could not post go now.
  useEffect(() => { flushPending(); }, []);

  // **Another tab cleared this device** — Delete my results, or the development
  // Reset — so this one starts again as well. Its device ID was removed out
  // from under it, which is the signal; a tab that carried on would show a
  // finished game and empty stats, and could write the old game or the old ID
  // back. A reload drops everything it holds in memory before anything is
  // written. An ID appearing where there was none is a first visit, not a
  // reset, and is left alone.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === DEVICE_KEY && e.oldValue && e.newValue !== e.oldValue) window.location.reload();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <>
      <SiteHeader onOpenHelp={() => openHowToPlay()}
                  onOpenArchive={() => setOpen('archive')} onOpenStats={() => setOpen('stats')} />

      <Routes>
        <Route path="/" element={<Index />} />
        {/* The old How it works pages, which shared links and search engines
            may still hold: the game itself, with How to play open on its tab. */}
        <Route path="/how-it-works/:game" element={<OldHowItWorks />} />
        {/* Today's daily, or one by number: asked of the server, which answers
            with the board and nothing more (src/lib/api.ts). */}
        <Route path="/running-order" element={<RunningOrderPage />} />
        <Route path="/running-order/:number" element={<RunningOrderPage />} />
        {/* The next Call Sheet this device has not finished, or one by number:
            asked of the server, which answers with what has been earned. */}
        <Route path="/call-sheet" element={<CallSheetPage />} />
        <Route path="/call-sheet/:number" element={<CallSheetPage />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/design" element={<DesignSystem />} />
        <Route path="*" element={
          <Notice message="That page doesn't exist." />} />
      </Routes>

      <SiteFooter />

      <Modal title="How to play" open={open === 'howto'} onClose={() => setOpen(null)}>
        <HowToPlayPanel initial={howTab} />
      </Modal>
      <Modal title="Archive" open={open === 'archive'} onClose={() => setOpen(null)} fixedHead>
        <ArchivePanel onClose={() => setOpen(null)} initial={game ?? 'running-order'} />
      </Modal>
      <Modal title="Your stats" open={open === 'stats'} onClose={() => setOpen(null)}>
        <StatsPanel onClose={() => setOpen(null)} initial={game ?? 'running-order'} />
      </Modal>
    </>
  );
}

// `/how-it-works/running-order` and `/how-it-works/call-sheet` go to that game's
// puzzle with the How to play window open on its tab (owner, 12 October); any
// other game name is an address that does not exist.
function OldHowItWorks() {
  const { game } = useParams();
  if (game === 'running-order' || game === 'call-sheet') {
    return <Navigate to={`/${game}`} replace state={{ howToPlay: true }} />;
  }
  return <Notice message="That page doesn't exist." />;
}
